import type { LinkProps } from "react-router-dom";

import type { IRouteLocationState } from "./link.types";

/**
 * The shape of react-router's `to` — a location descriptor or a resolver
 * function. Sourced from `LinkProps` so core needn't depend on `history` types.
 */
type TLinkTo = LinkProps["to"];

/**
 * The "magic" class marking our custom z-index/content root, used as a scroll
 * container when no antd modal/drawer/popover ancestor is found. Inlined from
 * `Clients.App/src/types/constants` (`BTContentRoot`) so the scroll helper stays
 * self-contained in core.
 */
const BT_CONTENT_ROOT = "bt-content-root";

/**
 * Finds the closest scroll container (antd modal/drawer/popover, fullscreen
 * element, or our content root) for the given node, so scrolling happens within
 * the correct stacking/scroll context rather than the whole window. Inlined from
 * `Clients.App/src/utilities/helpers` (`getClosestReactModal`); it depends only
 * on the DOM.
 */
function getClosestScrollContainer(
  triggerNode?: Element | null,
): HTMLElement | undefined {
  if (triggerNode) {
    const closestHeader = triggerNode.closest(".ant-layout-header");
    if (closestHeader !== null) {
      return closestHeader as HTMLElement;
    }

    const closestModal = triggerNode.closest(".ant-modal-body");
    if (closestModal !== null) {
      return closestModal as HTMLElement;
    }

    const closestDrawer = triggerNode.closest(".ant-drawer-body");
    if (closestDrawer !== null) {
      // Bottom sheets are anchored to the viewport bottom and tend to be short —
      // keeping popups inside the sheet would clip them, so let them portal to
      // body and overflow the sheet naturally.
      const isBottomSheet = closestDrawer.closest(".bt-bottom-sheet") !== null;
      if (!isBottomSheet) {
        return closestDrawer as HTMLElement;
      }
    }

    const closestPopover = triggerNode.closest(".ant-popover-body");
    if (closestPopover !== null) {
      return closestPopover as HTMLElement;
    }

    const closestFullscreenElement = triggerNode.closest(".Fullscreen");
    if (closestFullscreenElement !== null) {
      return closestFullscreenElement as HTMLElement;
    }

    const closestZIndexContentRoot = triggerNode.closest(`.${BT_CONTENT_ROOT}`);
    if (closestZIndexContentRoot !== null) {
      return closestZIndexContentRoot as HTMLElement;
    }
  }

  return undefined;
}

function scrollWithinRef(
  element: HTMLElement | (Window & typeof globalThis),
  amount: number,
  smooth?: boolean,
) {
  const supportsNativeSmoothScroll =
    "scrollBehavior" in document.documentElement.style;
  if (supportsNativeSmoothScroll) {
    element.scrollTo(
      smooth ? { top: amount, behavior: "smooth" } : { top: amount },
    );
  } else {
    element.scrollTo(0, amount);
  }
}

/**
 * Scrolls to a specific element on the page, honoring the nearest scroll
 * container. Inlined from `Clients.App/src/utilities/scroll/scroll` for the
 * `linkType="scroll"` behavior; depends only on the DOM.
 */
function scrollToElement(element: HTMLElement | null, center?: boolean) {
  if (element) {
    const closestScrollContainer = getClosestScrollContainer(element);
    const elementBoundingRect = element.getBoundingClientRect();

    let context: HTMLElement | (Window & typeof globalThis);
    let heightToScroll: number;
    let viewHeight: number;
    if (closestScrollContainer) {
      context = closestScrollContainer;
      const firstChild = context.children[0];
      heightToScroll =
        elementBoundingRect.top -
        (firstChild?.getBoundingClientRect().top ?? 0);
      viewHeight = context.offsetHeight;
    } else {
      context = window;
      heightToScroll = elementBoundingRect.top + context.pageYOffset;
      viewHeight = context.innerHeight;
    }

    if (center && viewHeight > elementBoundingRect.height) {
      heightToScroll =
        heightToScroll - (viewHeight - elementBoundingRect.height) / 2;
    }

    scrollWithinRef(context, heightToScroll, true);
  }
}

/**
 * Scrolls to a specific element on the page by its id. Used by BTLink's
 * `linkType="scroll"`.
 */
export function scrollToElementById(id: string, center?: boolean) {
  const element = document.getElementById(id);
  scrollToElement(element, center);
}

/**
 * True when the given `to` is a previous-location breadcrumb object (has both
 * `pathname` and `state`). Inlined from
 * `Clients.App/src/utilities/flatRoutes/usePreviousLocation`.
 */
function isPreviousLocation(to: TLinkTo | undefined) {
  return (
    typeof to !== "string" &&
    typeof to === "object" &&
    to !== null &&
    "pathname" in to &&
    "state" in to
  );
}

/**
 * Determines if a route is an {@link IRouteLocationState} object (carries a
 * `previous` breadcrumb) rather than a plain string/path. Inlined from
 * `Clients.App/src/utilities/flatRoutes/routes`; a pure type guard.
 */
export function isRouteLocationState(
  route: TLinkTo | undefined,
): route is IRouteLocationState {
  return (
    isPreviousLocation(route) &&
    "previous" in (route as IRouteLocationState).state
  );
}
